<template>
    <div v-if="needsPortalUrl" class="float-end d-flex align-items-end gap-2">
        <div class="thetalake-portal text-start">
            <label class="form-label small mb-1" :for="portalInputId">Theta Lake Portal URL</label>
            <div class="input-group input-group-sm">
                <input :id="portalInputId"
                       v-model="portalInput"
                       type="text"
                       class="form-control"
                       placeholder="preview1"
                       autocomplete="off"
                       spellcheck="false"
                       :aria-describedby="portalSuffixId"
                       @keyup.enter="connectDisconnect">
                <span class="input-group-text" :id="portalSuffixId">.{{ thetalakeDomain }}</span>
            </div>
        </div>
        <button type="button" @click.stop.prevent="connectDisconnect"
                class="btn btn-sm btn-success" :disabled="!portalUrl" :title="buttonTitle">
            {{ label }}
        </button>
    </div>
    <button v-else type="button" @click.stop.prevent="connectDisconnect" :class="{
                'float-end': true, btn:true, 'btn-sm': true,
                'btn-success': connected === false,
                'btn-danger': connected === true
            }">
        {{ label }}
    </button>
</template>

<style scoped>
    /* Wide enough that a realistic tenant name is readable next to the suffix; the
       float-end wrapper would otherwise shrink the input to its placeholder. */
    .thetalake-portal {
        min-width: 14rem;
    }
</style>

<script>
    import {mapGetters} from 'vuex'
    import {setPortalUrl} from '@/services/ThetalakePortalUrl'

    // DEV-2908: Theta Lake's authorize endpoint requires `theta_lake_portal_url` — the base URL
    // of the *customer's own* instance. Their documentation is explicit that customers know it:
    // "Every Theta Lake customer knows their portal URL." Only the subdomain varies in their
    // examples (`useast.thetalake.ai`, `uswest.thetalake.ai`, and preview's `preview1`), so we
    // ask for that label and hold the domain constant — a free-text URL field invites pointing
    // at developer.thetalake.ai, our developer site, where a customer has no account at all.
    //
    // ⚠ This cannot express a portal whose host does not match `<label>.thetalake.ai`. Pasting
    // such a host gets its dots stripped and the domain appended, yielding nonsense. The fix is
    // to accept a bare label *or* a full hostname rather than assuming a domain — see DEV-2908
    // Notes → *The portal field assumes a domain*. Not urgent while every known instance fits
    // the pattern, but it is a customer-facing dead end for one that does not.
    const THETALAKE_DOMAIN = 'thetalake.ai';

    /**
     * Reduce whatever the user typed to a bare instance label.
     *
     * Accepts `useast`, `useast.thetalake.ai`, or a full pasted portal URL, because all three
     * are things someone reasonably types into a field labelled "URL". Anything that is not a
     * valid DNS label character is dropped rather than escaped, so a malformed entry yields an
     * empty value and Connect stays disabled instead of sending Theta Lake a bad portal URL.
     */
    function instanceFromInput(raw) {
        return String(raw || '')
            .trim()
            .toLowerCase()
            .replace(/^[a-z]+:\/\//, '')          // scheme
            .split('/')[0]                         // path / fragment
            .split(':')[0]                         // port
            .replace(new RegExp(`\\.?${THETALAKE_DOMAIN.replace('.', '\\.')}$`), '')
            .replace(/[^a-z0-9-]/g, '')            // anything not a DNS label char
            .replace(/^-+|-+$/g, '');              // leading/trailing hyphens
    }

    // The template has two mutually exclusive roots on purpose. Every other integration keeps
    // the bare <button> as this component's root element, so class passthrough (PowerupCard
    // passes `full-width`), IntegrationItem's `$el.classList.add('float-end')`, and the
    // `.integration-list>li>.float-end` mobile rule all behave exactly as before. Only Theta
    // Lake's pre-connect state introduces a wrapper.
    //
    // Keep the template free of root-level comments: a comment node counts toward the root
    // count, which turns the v-if/v-else chain into a fragment and silently disables Vue 3's
    // automatic attribute inheritance.

    // Distinguishes instances for label/input `for` association. A module-level counter rather
    // than Vue's internal `_uid`, which is a Vue 2 field this app (Vue 3 via @vue/compat) does
    // not expose.
    let instanceSeq = 0;

    export default {
        props: ['service', 'team', 'beforeAuth'],
        data() {
            return {
                // Prefilled from env so QA does not retype it on every run. Normalized through
                // the same path as user input, so a full URL in the var still reduces cleanly.
                portalInput: instanceFromInput(process.env.VUE_APP_THETALAKE_TENANT),
                uid: ++instanceSeq
            }
        },
        computed: {
            connected() { return !!this.service && !!this.service.isConnected(this.team) },
            ...mapGetters({
                vendor: 'vendor',
                afterOauthData: 'afterOauthData'
            }),
            /**
             * DEV-2908: Theta Lake needs the customer's portal URL before authorization can
             * start, so a disconnected Theta Lake account gets an input alongside Connect.
             *
             * Keyed off the service's apiName rather than a new prop, so every existing caller
             * (Mailchimp, HubSpot, Sniply…) keeps its plain two-state button with no change at
             * the call site.
             */
            needsPortalUrl() {
                return !this.connected && !!this.service && this.service.apiName === 'thetalake';
            },
            portalUrl() {
                const instance = instanceFromInput(this.portalInput);
                return instance ? `https://${instance}.${THETALAKE_DOMAIN}` : '';
            },
            thetalakeDomain() { return THETALAKE_DOMAIN },
            // Unique per instance: the integrations list renders one of these per account, so a
            // static id would break label/input association for every row after the first.
            portalInputId() { return `thetalake-portal-${this.uid}` },
            portalSuffixId() { return `thetalake-portal-suffix-${this.uid}` },
            buttonTitle() {
                if (!this.needsPortalUrl) return null;
                return this.portalUrl
                    ? `Connect to ${this.portalUrl}`
                    : 'Enter your Theta Lake portal URL first.';
            },
            label() {
                return this.connected ? 'Disconnect' : 'Connect';
            }
        },
        methods: {
            async connectDisconnect() {
                if (this.connected) {
                    this.service.disconnect(this.team);
                    return;
                }

                if (this.needsPortalUrl) {
                    // Guard rather than rely on :disabled alone — the input's Enter handler
                    // reaches this method directly.
                    if (!this.portalUrl) return;
                    // Stored, not passed: the authorize round trip to Theta Lake sits between
                    // here and the callback, and their redirect returns `code`, `api_url` and
                    // `state` — never the portal URL. See ThetalakePortalUrl.js.
                    setPortalUrl(this.portalUrl);
                }

                if (this.beforeAuth) await this.beforeAuth();
                let iframed = this.vendor === 'hootsuite'
                setTimeout(() => this.service.connect(this.team, { iframed, afterOauthData: this.afterOauthData }), 5);
            }
        }
    }
</script>
